import React from 'react';
import {SubmitHandler, useForm} from 'react-hook-form';
import {useSearchParams} from 'react-router-dom';
import {DateRange} from 'rsuite/DateRangePicker';
import {t} from '@lingui/core/macro';

import ModalBodyWrapper from 'shared/ui/Modal/ModalBodyWrapper';
import FormGroupWithError from 'shared/ui/FormGroup/FormGroupWithError';
import ModalBlockButton from 'shared/ui/Button/ModalBlockButton/ModalBlockButton';
import Select from 'shared/ui/Select/Select';
import DateRangePickerControlled from 'shared/ui/Date/DateRangePickerControlled/DateRangePickerControlled';
import IServerOutput from 'features/server/model/Server/IServer';
import {IServerAlertRuleMetricOption} from 'features/server/model/ServerAlertRule/IServerAlertRuleRecommendations';
import {getServerAlertRuleSeverityLabel} from 'features/server/lib/serverAlertRuleHelper';
import {
    ALL_FILTER_VALUE,
    allValueToUndefined,
    dateRangeToFilters,
    setSearchParamsFromFilters,
    stringsToDateRange,
} from 'shared/lib/filterHelper';
import {getServerIncidentFilters} from 'features/server/lib/serverFilters';

const STATE_OPEN = 'open';
const STATE_CLOSED = 'closed';

function closedFilterToState(value: string | undefined): string {
    if (value === 'false') return STATE_OPEN;
    if (value === 'true') return STATE_CLOSED;
    return ALL_FILTER_VALUE;
}

function stateToClosedFilter(state: string): string | undefined {
    if (state === STATE_OPEN) return 'false';
    if (state === STATE_CLOSED) return 'true';
    return undefined;
}

interface IServerIncidentsFiltersFormProps {
    servers: IServerOutput[];
    metricOptions: IServerAlertRuleMetricOption[];
    onClose: () => void;
}

interface IServerIncidentsFiltersForm {
    server: string;
    metric: string;
    severity: string;
    state: string;
    opened_at?: DateRange;
    closed_at?: DateRange;
}

const ServerIncidentsFiltersForm: React.FC<IServerIncidentsFiltersFormProps> = ({
    servers,
    metricOptions,
    onClose,
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const filters = getServerIncidentFilters(searchParams);

    const presetState = closedFilterToState(filters['exists[closed_at]']);

    const {handleSubmit, control} = useForm<IServerIncidentsFiltersForm>({
        defaultValues: {
            server: filters['server.uuid'] ?? ALL_FILTER_VALUE,
            metric: filters.metric ?? ALL_FILTER_VALUE,
            severity: filters.severity ?? ALL_FILTER_VALUE,
            state: presetState,
            opened_at: stringsToDateRange(filters['opened_at[after]'], filters['opened_at[before]']),
            closed_at: stringsToDateRange(filters['closed_at[after]'], filters['closed_at[before]']),
        },
    });

    const serverOptions = [
        {value: ALL_FILTER_VALUE, label: t`Все серверы`},
        ...servers.map(server => ({value: server.uuid, label: server.name})),
    ];

    const metricSelectOptions = [
        {value: ALL_FILTER_VALUE, label: t`Любая метрика`},
        ...metricOptions.map(metric => ({value: metric.value, label: metric.label})),
    ];

    const severityOptions = [
        {value: ALL_FILTER_VALUE, label: t`Любая важность`},
        {value: 'warning', label: getServerAlertRuleSeverityLabel('warning')},
        {value: 'critical', label: getServerAlertRuleSeverityLabel('critical')},
    ];

    const stateOptions = [
        {value: ALL_FILTER_VALUE, label: t`Любой статус`},
        {value: STATE_OPEN, label: t`Открыт`},
        {value: STATE_CLOSED, label: t`Закрыт`},
    ];

    const onSubmit: SubmitHandler<IServerIncidentsFiltersForm> = ({
        server,
        metric,
        severity,
        state,
        // eslint-disable-next-line camelcase
        opened_at,
        // eslint-disable-next-line camelcase
        closed_at,
    }) => {
        const data: Record<string, string | undefined> = {
            'server.uuid': allValueToUndefined(server),
            metric: allValueToUndefined(metric),
            severity: allValueToUndefined(severity),
            'exists[closed_at]': stateToClosedFilter(state),
        };
        dateRangeToFilters(opened_at, data, 'opened_at');
        dateRangeToFilters(closed_at, data, 'closed_at');

        setSearchParamsFromFilters(searchParams, data);
        searchParams.set('page', '1');
        setSearchParams(searchParams);
        onClose();
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <ModalBodyWrapper>
                <FormGroupWithError label={t`Сервер`}>
                    <Select control={control} name="server" data={serverOptions} searchable={servers.length > 5}/>
                </FormGroupWithError>

                <FormGroupWithError label={t`Метрика`}>
                    <Select
                        control={control}
                        name="metric"
                        data={metricSelectOptions}
                        searchable={metricOptions.length > 5}
                    />
                </FormGroupWithError>

                <FormGroupWithError label={t`Важность`}>
                    <Select control={control} name="severity" data={severityOptions} searchable={false}/>
                </FormGroupWithError>

                <FormGroupWithError label={t`Статус`}>
                    <Select control={control} name="state" data={stateOptions} searchable={false}/>
                </FormGroupWithError>

                <FormGroupWithError label={t`Период открытия инцидента`}>
                    <DateRangePickerControlled control={control} name="opened_at" cleanable={true}/>
                </FormGroupWithError>

                <FormGroupWithError label={t`Период закрытия инцидента`}>
                    <DateRangePickerControlled control={control} name="closed_at" cleanable={true}/>
                </FormGroupWithError>
            </ModalBodyWrapper>
            <ModalBlockButton
                primaryButtonText={t`Применить фильтры`}
                primaryButtonType="submit"
                secondaryButtonOnClick={onClose}
            />
        </form>
    );
};

export default ServerIncidentsFiltersForm;
